'use client'

/**
 * Social proof for the /subscribe page (a client component, so it can't render the server
 * CaughtEarlyStrip). Fetches the scanner's biggest DATED "caught early" receipts across the last ~30
 * sessions — our earliest alert price/time vs that day's intraday high — as receipt cards: concrete,
 * verifiable proof of the edge the subscription buys. Pulling recent sessions (not just today) means
 * the proof is ALWAYS populated and strong, even off-hours or on a quiet day. Renders nothing until it
 * has real wins, so it never shows an empty or fabricated state. These are notable dated examples, not
 * a hit-rate or profit promise — the disclaimer says so.
 */

import { useEffect, useState } from 'react'
import { TrendingUp } from 'lucide-react'

type Row = { symbol: string; name: string | null; dateLabel: string; alertTime: string; alertPrice: number; high: number; runPct: number }

const fmtPx = (n: number) => (n < 1 ? `$${n.toFixed(3)}` : `$${n.toFixed(2)}`)

export default function SubscribeProof({ compact = false }: { compact?: boolean }) {
  const [rows, setRows] = useState<Row[] | null>(null)
  // Non-null once we know we are showing the PERSONALIZED "since you joined" set (and how many days).
  const [personalDays, setPersonalDays] = useState<number | null>(null)

  useEffect(() => {
    let alive = true
    ;(async () => {
      // Prefer the PERSONALIZED "since you joined" receipts for a signed-in taster: their OWN window is the
      // most undeniable proof at the moment of the ask (make-value-undeniable-0918). The since-join endpoint
      // returns nothing for subscribers (no nudge, subscriber_nudge_policy), logged-out visitors, or a too-
      // new account, so those fall through to the generic recent set unchanged. Require >=2 so one alone can
      // never read as a cherry-pick (same threshold the trial-ending email uses).
      try {
        const r = await fetch('/api/caught-early?scope=since-join', { cache: 'no-store' })
        if (r.ok) {
          const j = await r.json()
          const pr: Row[] = Array.isArray(j?.rows) ? j.rows.filter((x: Row) => x.runPct >= 15) : []
          if (alive && pr.length >= 2) { setRows(pr); setPersonalDays(Number(j?.days) || 0); return }
        }
      } catch { /* fall through to generic */ }
      if (!alive) return
      // Generic fallback: the biggest dated receipts across the last ~30 sessions.
      try {
        const r = await fetch('/api/caught-early?scope=recent', { cache: 'no-store' })
        if (r.ok) {
          const j = await r.json()
          if (alive && Array.isArray(j?.rows)) setRows(j.rows.filter((x: Row) => x.runPct >= 15))
        }
      } catch { /* leave null → render nothing */ }
    })()
    return () => { alive = false }
  }, [])

  if (!rows || rows.length === 0) return null
  const top = rows.slice(0, compact ? 3 : 6)
  // Owner-approved 2026-09-19: the personalized header uses the trial-ending email's "since you joined"
  // voice (not a day-count), so a taster sees their OWN window's proof right at the ask.
  const heading = personalDays !== null
    ? 'Since you joined, the scanner caught these before they ran'
    : 'Recently caught early by the scanner'

  return (
    <div className={compact ? 'w-full' : 'mx-auto mt-8 max-w-2xl'}>
      <div className={`mb-2 flex items-center gap-2 font-semibold uppercase tracking-widest text-emerald-700 dark:text-emerald-300/80 ${compact ? 'justify-start text-[10px]' : 'mb-3 justify-center text-xs'}`}>
        <TrendingUp className="h-3.5 w-3.5" /> {heading}
      </div>
      <div className={`grid grid-cols-1 gap-2 ${compact ? '' : 'sm:grid-cols-2'}`}>
        {top.map((r) => (
          <div key={`${r.symbol}-${r.dateLabel}`} className="flex items-center gap-3 rounded-xl border border-border bg-card/40 px-3.5 py-2.5">
            <div className="min-w-0">
              <div className="flex items-baseline gap-1.5">
                <span className="text-sm font-bold text-foreground">${r.symbol}</span>
                <span className="text-[11px] text-muted-foreground">{r.dateLabel} · {r.alertTime} ET</span>
              </div>
              <div className="text-[11px] text-muted-foreground">
                Alerted at <span className="font-semibold text-foreground/80">{fmtPx(r.alertPrice)}</span>
                {' → ran to '}<span className="font-semibold text-foreground/80">{fmtPx(r.high)}</span>
              </div>
            </div>
            <span className="ml-auto shrink-0 rounded-md bg-emerald-50 dark:bg-emerald-500/15 px-2 py-1 text-sm font-bold tabular-nums text-emerald-700 dark:text-emerald-300">
              +{r.runPct.toFixed(0)}%
            </span>
          </div>
        ))}
      </div>
      <p className={`text-[11px] text-zinc-600 ${compact ? 'mt-2' : 'mt-2.5 text-center'}`}>{compact ? 'Real timestamped alerts, not trade advice.' : 'Real, timestamped alerts from recent sessions. Alerts flag unusual volume, not trade advice. Past performance is not a guarantee of future results.'}</p>
    </div>
  )
}
